import { useEffect, useState } from "react";
import { IntlProvider } from "react-intl";
import { Navigate, Outlet, useLocation, useParams } from "react-router-dom";
import {
  DEFAULT_LOCALE,
  LOCALE_DEFS,
  getLocaleDef,
  isSupported,
  negotiateLocale,
} from "./locales.js";
import { fallbackMessages, getMessages, handleIntlError } from "./messages.js";
import { LOCALE_KEY, readStorage } from "../lib/storage.js";
import { FullPageLoader } from "../components/ui.jsx";

/** Best locale for a first visit: explicit choice, then browser languages, then English. */
export function detectLocale() {
  const saved = readStorage(LOCALE_KEY);
  const langs = typeof navigator !== "undefined" ? navigator.languages || [navigator.language] : [];
  return negotiateLocale([saved, ...langs]);
}

function ensureFont(def) {
  if (!def.fontFamily) return;
  const id = `font-${def.code}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${def.fontFamily}&display=swap`;
  document.head.appendChild(link);
}

export function LocaleLayout() {
  const { locale } = useParams();
  const location = useLocation();
  const supported = isSupported(locale);
  const [state, setState] = useState(() =>
    locale === DEFAULT_LOCALE ? { locale, messages: { ...fallbackMessages } } : null,
  );

  useEffect(() => {
    if (!supported) return;
    let alive = true;
    if (state?.locale !== locale) {
      getMessages(locale).then((messages) => alive && setState({ locale, messages }));
    }
    const def = getLocaleDef(locale);
    document.documentElement.lang = locale;
    document.documentElement.dir = def.dir || "ltr";
    document.documentElement.dataset.locale = locale;
    ensureFont(def);
    return () => {
      alive = false;
    };
  }, [locale, supported, state?.locale]);

  if (!supported) {
    // A planned-but-unshipped locale replaces the segment; anything else is a path missing its prefix.
    const best = detectLocale();
    const isLocaleLike = LOCALE_DEFS.some((l) => l.code.toLowerCase() === String(locale).toLowerCase());
    const rest = isLocaleLike ? location.pathname.split("/").slice(2).join("/") : location.pathname.slice(1);
    return <Navigate to={`/${best}/${rest}${location.search}`} replace />;
  }

  if (!state || state.locale !== locale) return <FullPageLoader />;

  return (
    <IntlProvider
      locale={locale}
      defaultLocale={DEFAULT_LOCALE}
      messages={state.messages}
      onError={handleIntlError}
    >
      <Outlet />
    </IntlProvider>
  );
}

export function RootRedirect() {
  const location = useLocation();
  try {
    sessionStorage.setItem("pharos.autoLocale", "1");
  } catch {
    /* ignore */
  }
  const path = location.pathname === "/" ? "" : location.pathname.slice(1);
  return <Navigate to={`/${detectLocale()}/${path}${location.search}`} replace />;
}
